/**
 * Backend istemcisi. Access token yalnızca bellekte tutulur; refresh token httpOnly
 * cookie'dedir (JavaScript erişemez). 401'de tek seferlik yenileme yapılıp istek tekrarlanır.
 */

export interface ValidationIssue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  get issues(): ValidationIssue[] {
    return Array.isArray(this.details.issues) ? (this.details.issues as ValidationIssue[]) : [];
  }
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const issue = e.issues[0];
    return issue ? `${e.message}: ${issue.path ? `${issue.path} — ` : ''}${issue.message}` : e.message;
  }
  if (e instanceof Error) return e.message;
  return 'Beklenmeyen bir hata oluştu';
}

export type QueryValue = string | number | boolean | null | undefined | readonly (string | number)[];
export type Query = Record<string, QueryValue>;

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Yanıt JSON yerine Blob olarak döner (dosya indirme). */
  blob?: boolean;
  /** 401'de yenileme denenmesin (giriş uçları). */
  skipRefresh?: boolean;
}

export interface TokenResponse {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
}

export interface ApiClientOptions {
  /** Varsayılan '/api/v1'. */
  baseUrl?: string;
  /** Örn. '/auth/refresh' ya da '/platform/auth/refresh'. */
  refreshPath: string;
  /** Yenileme başarısız olunca (oturum düştü). */
  onSessionExpired?: () => void;
}

export type SseHandlers = Record<string, (data: unknown) => void>;

export function buildQuery(query?: Query): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length) params.set(k, v.join(','));
    } else params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function createApiClient(opts: ApiClientOptions) {
  const baseUrl = opts.baseUrl ?? '/api/v1';
  let token: string | null = null;
  let refreshing: Promise<boolean> | null = null;
  const listeners = new Set<(token: string | null) => void>();

  const setToken = (t: string | null) => {
    token = t;
    for (const l of listeners) l(t);
  };

  async function doRefresh(): Promise<boolean> {
    try {
      const res = await fetch(baseUrl + opts.refreshPath, { method: 'POST', credentials: 'same-origin' });
      if (!res.ok) {
        setToken(null);
        return false;
      }
      const data = (await res.json()) as TokenResponse;
      setToken(data.accessToken);
      return true;
    } catch {
      return false;
    }
  }

  /** Aynı anda gelen 401'ler tek yenileme isteği paylaşır. */
  function refresh(): Promise<boolean> {
    refreshing ??= doRefresh().finally(() => {
      refreshing = null;
    });
    return refreshing;
  }

  async function request<T>(method: string, path: string, o: RequestOptions = {}, retried = false): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json', ...o.headers };
    if (token) headers.Authorization = `Bearer ${token}`;
    let body: BodyInit | undefined;
    if (o.body instanceof Blob) {
      body = o.body;
      if (!headers['Content-Type'] && o.body.type) headers['Content-Type'] = o.body.type;
    } else if (o.body instanceof ArrayBuffer || ArrayBuffer.isView(o.body)) {
      body = o.body as BodyInit;
    } else if (o.body !== undefined) {
      body = JSON.stringify(o.body);
      headers['Content-Type'] = 'application/json';
    }

    let res: Response;
    try {
      res = await fetch(baseUrl + path + buildQuery(o.query), { method, headers, body, signal: o.signal, credentials: 'same-origin' });
    } catch (e) {
      if ((e as Error).name === 'AbortError') throw e;
      throw new ApiError(0, 'NETWORK_ERROR', 'Sunucuya ulaşılamadı. Bağlantınızı kontrol edin.');
    }

    if (res.status === 401 && !retried && !o.skipRefresh) {
      if (await refresh()) return request<T>(method, path, o, true);
      opts.onSessionExpired?.();
    }

    if (!res.ok) {
      let payload: Record<string, unknown> = {};
      try {
        payload = (await res.json()) as Record<string, unknown>;
      } catch {
        /* gövde JSON değil */
      }
      const { code, message, ...rest } = payload;
      throw new ApiError(
        res.status,
        typeof code === 'string' ? code : `HTTP_${res.status}`,
        typeof message === 'string' ? message : res.statusText || 'İstek başarısız',
        rest,
      );
    }
    if (res.status === 204) return undefined as T;
    if (o.blob) return (await res.blob()) as T;
    const type = res.headers.get('content-type') ?? '';
    if (type.includes('application/json')) return (await res.json()) as T;
    return (await res.text()) as T;
  }

  /**
   * Server-Sent Events (Authorization başlığıyla; EventSource başlık gönderemez).
   * Bağlantı koparsa artan beklemeyle yeniden bağlanır. Dönen fonksiyon kapatır.
   */
  function stream(path: string, query: Query, handlers: SseHandlers, onState?: (state: 'connecting' | 'open' | 'closed') => void): () => void {
    const controller = new AbortController();
    let attempt = 0;

    const run = async () => {
      while (!controller.signal.aborted) {
        onState?.('connecting');
        try {
          const headers: Record<string, string> = { Accept: 'text/event-stream' };
          if (token) headers.Authorization = `Bearer ${token}`;
          const res = await fetch(baseUrl + path + buildQuery(query), { headers, signal: controller.signal, credentials: 'same-origin' });
          if (res.status === 401) {
            if (!(await refresh())) {
              opts.onSessionExpired?.();
              break;
            }
            continue;
          }
          if (!res.ok || !res.body) throw new Error(`SSE ${res.status}`);
          attempt = 0;
          onState?.('open');
          await readEvents(res.body, handlers, controller.signal);
        } catch (e) {
          if (controller.signal.aborted) break;
          void e;
        }
        if (controller.signal.aborted) break;
        onState?.('closed');
        attempt++;
        await sleep(Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5)), controller.signal);
      }
      onState?.('closed');
    };
    void run();
    return () => controller.abort();
  }

  return {
    request,
    get: <T>(path: string, query?: Query, o?: Omit<RequestOptions, 'query'>) => request<T>('GET', path, { ...o, query }),
    post: <T>(path: string, body?: unknown, o?: RequestOptions) => request<T>('POST', path, { ...o, body }),
    put: <T>(path: string, body?: unknown, o?: RequestOptions) => request<T>('PUT', path, { ...o, body }),
    patch: <T>(path: string, body?: unknown, o?: RequestOptions) => request<T>('PATCH', path, { ...o, body }),
    del: <T = void>(path: string, o?: RequestOptions) => request<T>('DELETE', path, o),
    stream,
    refresh,
    setToken,
    getToken: () => token,
    onTokenChange(fn: (token: string | null) => void) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

async function readEvents(body: ReadableStream<Uint8Array>, handlers: SseHandlers, signal: AbortSignal) {
  const reader = body.pipeThrough(new TextDecoderStream() as unknown as ReadableWritablePair<string, Uint8Array>).getReader();
  let buffer = '';
  try {
    while (!signal.aborted) {
      const { value, done } = await reader.read();
      if (done) return;
      buffer += value;
      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) >= 0) {
        const chunk = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const parsed = parseEvent(chunk);
        if (!parsed) continue;
        const handler = handlers[parsed.event];
        if (!handler) continue;
        try {
          handler(JSON.parse(parsed.data));
        } catch {
          /* bozuk olay atlanır */
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export function parseEvent(chunk: string): { event: string; data: string } | null {
  let event = 'message';
  const data: string[] = [];
  for (const line of chunk.split('\n')) {
    if (!line || line.startsWith(':')) continue;
    const i = line.indexOf(':');
    const field = i < 0 ? line : line.slice(0, i);
    const value = i < 0 ? '' : line.slice(i + 1).replace(/^ /, '');
    if (field === 'event') event = value;
    else if (field === 'data') data.push(value);
  }
  return data.length ? { event, data: data.join('\n') } : null;
}

function sleep(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve) => {
    const t = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      clearTimeout(t);
      resolve();
    });
  });
}
